/**
 * Reports plan assignees (`PlanAssignees`, `reportsRoute === true` + plan report).
 * Maps XP {@link PlanUsersReport} + location users → {@link TeamMemberRow}.
 */
import { type PlanUsersReport } from '@cfa/pathway-fe-shared/xp';

import { getFilterableStatus } from '@/utils/getFilterableStatus';
import { getTimeSpentOnPlan } from '@/utils/time';

import Constants from '@/constants';

import type { TeamMemberRow } from '../teamMemberRow';

import {
  percentCompleteFromSteps,
  type PlanAssigneeNameSource,
  teamMemberNamesFromUser,
} from './planAssigneeRowShared';

interface PlanAssigneeLocationUser {
  locationList?: string[];
}

type PlanUserReportEntry = NonNullable<
  PlanUsersReport['planUserReports']
>[number];

const planUserReportEntryToTeamMemberRow = (
  { user, ...report }: PlanUserReportEntry,
  {
    locationUser,
    totalSteps,
  }: {
    locationUser?: PlanAssigneeLocationUser;
    totalSteps: number;
  },
): TeamMemberRow => {
  const status = report.status ?? '';
  const completedStepCount = report.stepsComplete ?? 0;

  return {
    ...teamMemberNamesFromUser(user as PlanAssigneeNameSource),
    id: user?.id ?? '',
    userId: user?.id,
    completionDate: report.completionDate,
    dueDate: report.dueDate,
    status,
    locations: locationUser?.locationList,
    filterStatus: getFilterableStatus(status, false),
    previouslyCompletedPlanAssignmentDate:
      report.latestCompletionReport?.completionDate ??
      report.completionDate ??
      '',
    timeSpentOnPlan: getTimeSpentOnPlan({
      timeTaken: report.timeTakenInSeconds,
    }),
    percentComplete: percentCompleteFromSteps(completedStepCount, totalSteps),
    completedStepCount,
  };
};

export const planUserReportsToTeamMemberRows = (
  planReport: PlanUsersReport,
  usersById: Map<string, PlanAssigneeLocationUser>,
): TeamMemberRow[] => {
  const totalSteps = planReport.totalSteps ?? 0;

  return (planReport.planUserReports ?? [])
    .filter(entry => entry.status !== Constants.TRAINING_PLANS.UNASSIGNED)
    .map(entry => {
      const locationUser = usersById.get(entry.user?.id ?? '');
      return planUserReportEntryToTeamMemberRow(entry, {
        locationUser,
        totalSteps,
      });
    });
};
